import {
  BusinessGroupId,
  CMSMetadata,
  MarketPlace,
  PageId,
  PageType,
  Placement,
  Provider,
  Slot,
  Template,
  CTA,
  TransformationTemplate,
  AssetType,
  ProviderLogo,
  LocaleEnum,
} from '@amzn/fire-tv-campaign-automation-service';
import { ProvisionedConstants } from 'src/constants';
import { PropUtils } from 'src/utils/PropUtils';

type MetadataItemType = 'text' | 'date-time' | 'drop-down';

interface BaseMetadataConfig {
  type: MetadataItemType;
  label?: string;
  placeholder: string;
  dataTestId: string;
  required?: boolean;
  rowGroup?: string; // Items with the same rowGroup will be rendered in the same row
  width?: 'half' | 'full'; // Default to full if not specified
}

export interface TextMetadataConfig extends BaseMetadataConfig {
  type: 'text';
  minLength?: number;
  maxLength?: number;
}

export interface DateTimeMetadataConfig extends BaseMetadataConfig {
  type: 'date-time';
}

export interface DropDownMetadataConfig extends BaseMetadataConfig {
  type: 'drop-down';
  options: Array<{ name: string; value: string }>;
}

export type MetadataConfig = TextMetadataConfig | DateTimeMetadataConfig | DropDownMetadataConfig;

/**
 * Maps page IDs to their available slot configurations.
 * Parsed from the katal manifests replacement constants.
 */
const PAGE_ID_TO_SLOT_CONFIGURATIONS: Record<PageId, Slot[]> =
  process.env.NODE_ENV === 'test'
    ? ({} as Record<PageId, Slot[]>)
    : (() => {
        try {
          return JSON.parse(ProvisionedConstants.SLOT_CONFIGURATIONS);
        } catch (error) {
          console.error('Failed to parse slot configurations:', error);
          return {} as Record<PageId, Slot[]>; // Fallback to empty configuration
        }
      })();

interface CMSConfigType {
  TRANSFORMATION_TEMPLATE: Record<AssetType, TransformationTemplate[]>;
  CTA: Record<MarketPlace, CTA[]>;
  TEMPLATES: Template[];
  PLACEMENTS: Placement[];
  BUSINESS_GROUP_IDS: BusinessGroupId[];
  PAGE_TYPES: PageType[];
  MARKET_PLACE: MarketPlace[];
  PROVIDER_LOGO: ProviderLogo[];
  LOCALE: Record<MarketPlace, LocaleEnum[]>;
}

/**
 * Comprises the stage specific CMS Configuration map
 */
export const CMSConfig: CMSConfigType =
  process.env.NODE_ENV === 'test'
    ? ({
        BUSINESS_GROUP_IDS: [BusinessGroupId.Bullseye_Users],
        PAGE_TYPES: [PageType.BROWSE, PageType.DETAIL, PageType.SEARCH],
        MARKET_PLACE: [MarketPlace.US],
        TEMPLATES: [Template.READY_ASSETS],
        TRANSFORMATION_TEMPLATE: {
          [AssetType.BACKGROUND_IMAGE]: [
            TransformationTemplate.NO_TRANSFORMATION,
            TransformationTemplate.DOMINANT_COLOR_FADED_OVERLAY,
          ],
          [AssetType.LOGO]: [TransformationTemplate.NO_TRANSFORMATION],
          [AssetType.VIDEO_TRAILER]: [TransformationTemplate.NO_TRANSFORMATION],
        },
        CTA: {
          [MarketPlace.US]: [
            CTA.WATCH_FREE,
            CTA.PLAY_NOW,
            CTA.RECOMMENDED_FOR_YOU,
            CTA.STREAM_NOW,
            CTA.WATCH_FREE_ON,
            CTA.WATCH_LIVE,
            CTA.WATCH_NOW,
          ],
        },
        PROVIDER_LOGO: [
          ProviderLogo.FIRE_TV,
          ProviderLogo.FIRE_CHANNELS,
          ProviderLogo.PRIME_VIDEO,
          ProviderLogo.BET,
          ProviderLogo.AMC_PLUS,
          ProviderLogo.TUBI,
        ],
        LOCALE: {
          [MarketPlace.US]: [LocaleEnum.EN_US],
        },
      } as CMSConfigType)
    : (() => {
        try {
          return JSON.parse(ProvisionedConstants.CMS_CONFIG);
        } catch (error) {
          console.error('Failed to parse cms configurations:', error);
          return {} as CMSConfigType; // Fallback to empty configuration
        }
      })();

export const CAMPAIGN_METADATA_CONFIG: Partial<Record<keyof CMSMetadata, MetadataConfig>> = {
  title: {
    type: 'text',
    label: 'Title',
    placeholder: 'Enter campaign title',
    dataTestId: 'title-input',
    required: true,
    rowGroup: 'row1',
    minLength: 1,
    maxLength: 150,
  },
  businessGroupId: {
    type: 'drop-down',
    label: 'Business Group',
    placeholder: 'Select business group',
    dataTestId: 'business-group-dropdown',
    rowGroup: 'row2',
    width: 'half',
    options: PropUtils.getOptionsWithUnderscoresRemoved(
      PropUtils.createOptionsFromEnumValues(CMSConfig.BUSINESS_GROUP_IDS, BusinessGroupId),
    ),
  },
  pageId: {
    type: 'drop-down',
    label: 'Page ID',
    placeholder: 'Select page ID',
    dataTestId: 'page-id-dropdown',
    rowGroup: 'row2',
    width: 'half',
    options: PropUtils.createOptionsFromEnum(PageId),
  },
  provider: {
    type: 'drop-down',
    label: 'Provider',
    placeholder: 'Select provider',
    dataTestId: 'provider-dropdown',
    rowGroup: 'row4',
    width: 'half',
    options: PropUtils.createOptionsFromEnum(Provider),
  },
  slotName: {
    type: 'drop-down',
    label: 'Slot',
    placeholder: 'Select slot',
    dataTestId: 'slot-dropdown',
    rowGroup: 'row4',
    width: 'half',
    options: PropUtils.createOptionsFromEnum(Slot),
  },
  campaignStartDate: {
    type: 'date-time',
    placeholder: 'Select start date and time',
    dataTestId: 'campaign-start-date-input',
    required: true,
  },
  campaignEndDate: {
    type: 'date-time',
    placeholder: 'Select end date and time',
    dataTestId: 'campaign-end-date-input',
    required: true,
  },
  clickThrough: {
    type: 'text',
    label: 'Click Through URL',
    placeholder: 'ASIN / Url',
    dataTestId: 'click-through-input',
    required: true,
    rowGroup: 'row7',
    minLength: 10,
    maxLength: 2048,
  },
  accessibilityText: {
    type: 'text',
    label: 'Accessibility Text',
    placeholder: 'Enter accessibility text',
    dataTestId: 'accessibility-text-input',
    rowGroup: 'row8',
    minLength: 1,
    maxLength: 150,
    required: true,
  },
};

/**
 * Retrieves slot options for a given page ID formatted for dropdown selection.
 * These options are used to populate the slot dropdown in the campaign metadata form.
 *
 * @param pageId - The page ID to get slot options for
 * @returns Array of slot options formatted as dropdown options with name and value properties
 */
export function getSlotOptionsForPageId(pageId: string): { name: string; value: string }[] {
  // Validate if pageId is a valid PageId enum value
  if (!Object.values(PageId).includes(pageId as PageId)) {
    return [];
  }

  // Access with fallback to empty array if configuration doesn't exist
  const slotConfig = PAGE_ID_TO_SLOT_CONFIGURATIONS[pageId as PageId] || [];
  return PropUtils.createOptionsFromEnumValues(slotConfig);
}

export { targetSegmentConfiguration } from './targetSegmentConfiguration';
